// src/modules/demo/components/DemoDrawer.tsx

import { useCallback, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import type { SelectChangeEvent } from '@mui/material/Select';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TranslateIcon from '@mui/icons-material/Translate';

import Header from '../../../layout/app-bar/AppBar';
import {
  ParticifyLogo,
  TrakifyLogo,
  CalendFyLogo,
  NotifyLogo,
  LogifyLogo,
  ReportifyLogo,
} from '../../../components/logos/ModuleLogos';
import { drawerWidth } from '../../../constants/constants';
import { useTranslationsContext } from '../../../contexts/TranslationsContext';
import { Languages } from '../../../types';
import { useDemoData } from '../context/DemoDataContext';
import { useDemoAnalytics } from '../hooks/useDemoAnalytics';
import DEMO_TRANSLATIONS from '../translations/demo.translations';

import './DemoDrawer.css';

export default function DemoDrawer() {
  const location = useLocation();
  const { language, setLanguage } = useTranslationsContext();
  const { state, resetDemo } = useDemoData();
  const { trackOnce } = useDemoAnalytics();
  const [mobileOpen, setMobileOpen] = useState(false);
  const t = language === Languages.ENGLISH ? DEMO_TRANSLATIONS.EN : DEMO_TRANSLATIONS.DE;

  const isDemoHome = location.pathname === '/demo';

  useEffect(() => {
    document.documentElement.style.setProperty('--drawer-left-space', `${drawerWidth}px`);
  }, []);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  function handleReset() {
    trackOnce('demo_reset');
    resetDemo();
  }

  function handleLanguageChange(e: SelectChangeEvent<string>) {
    setLanguage(e.target.value === 'EN' ? Languages.ENGLISH : Languages.DEUTSCH);
  }

  const disabledModules = [
    { name: 'TrackiFy', logo: <TrakifyLogo size={20} /> },
    { name: 'ReportiFy', logo: <ReportifyLogo size={20} /> },
    { name: 'LogiFy', logo: <LogifyLogo size={20} /> },
    { name: 'CalendiFy', logo: <CalendFyLogo size={20} /> },
    { name: 'NotiFy', logo: <NotifyLogo size={20} /> },
  ];

  const drawerContent = (
    <div className="demo-drawer-container">
      <Toolbar />

      {/* Logo + DEMO badge */}
      <div className="demo-drawer-logo">
        <svg width="22" height="22" viewBox="0 0 50 50" fill="none">
          <rect width="50" height="50" rx="12" fill="#7aacb3" />
          <path d="M25 10L7 19l18 9 18-9-18-9z" fill="white" opacity="0.9" />
          <path d="M13 24v9c0 3.3 5.4 6 12 6s12-2.7 12-6v-9L25 30 13 24z" fill="white" opacity="0.6" />
        </svg>
        <span className="demo-drawer-logo-text">TutoriFy</span>
        <span className="demo-drawer-badge">{t.DEMO_BADGE}</span>
      </div>

      {/* Class display */}
      <div className="demo-drawer-class">
        <span className="demo-drawer-class-name">{state.classroom.name}</span>
        {' · '}{state.classroom.subject}
      </div>

      <Divider sx={{ opacity: 0.06 }} />

      {/* Module list */}
      <List>
        {/* ParticiFy — active */}
        <ListItem disablePadding>
          <Link className="w-full" to="/demo">
            <ListItemButton
              selected={isDemoHome}
              onClick={() => mobileOpen && setMobileOpen(false)}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <div style={{ display: 'flex' }}>
                  <ParticifyLogo size={20} />
                </div>
              </ListItemIcon>
              <ListItemText
                primary="ParticiFy"
                primaryTypographyProps={{
                  color: isDemoHome ? 'primary' : 'inherit',
                  fontWeight: isDemoHome ? 600 : 400,
                  fontSize: '0.84rem',
                }}
              />
              {isDemoHome && <span className="drawer-nav-dot" data-active="true" />}
            </ListItemButton>
          </Link>
        </ListItem>

        {/* Disabled modules */}
        {disabledModules.map((mod) => (
          <ListItem key={mod.name} disablePadding>
            <Tooltip title={t.AVAILABLE_AFTER_SIGNUP} placement="right" arrow>
              <span className="w-full">
                <ListItemButton disabled>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <div style={{ display: 'flex', opacity: 0.35 }}>{mod.logo}</div>
                  </ListItemIcon>
                  <ListItemText
                    primary={mod.name}
                    primaryTypographyProps={{ fontSize: '0.84rem' }}
                  />
                </ListItemButton>
              </span>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      <div className="demo-drawer-spacer" />

      <Divider sx={{ opacity: 0.06 }} />

      {/* Language selector */}
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <TranslateIcon sx={{ fontSize: 16, opacity: 0.4 }} />
        <Select
          value={language === Languages.ENGLISH ? 'EN' : 'DE'}
          onChange={handleLanguageChange}
          size="small"
          sx={{
            fontSize: '0.78rem',
            color: 'rgba(255,255,255,0.7)',
            flex: 1,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
            '& .MuiSelect-select': { py: '4px' },
          }}
        >
          <MenuItem value="DE">Deutsch</MenuItem>
          <MenuItem value="EN">English</MenuItem>
        </Select>
      </div>

      {/* Actions */}
      <div className="demo-drawer-actions">
        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
          onClick={handleReset}
          sx={{
            textTransform: 'none',
            fontSize: '0.78rem',
            borderColor: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.6)',
            borderRadius: '8px',
            '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
          }}
        >
          {t.RESET_DEMO}
        </Button>
        <Link to="/auth/signup">
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => trackOnce('demo_cta_signup_clicked')}
            sx={{
              textTransform: 'none',
              fontSize: '0.82rem',
              fontWeight: 700,
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #7aacb3 0%, #5e9aa1 100%)',
              boxShadow: '0 4px 16px rgba(122,172,179,0.25)',
              '&:hover': { background: 'linear-gradient(135deg, #8bbdc4 0%, #7aacb3 100%)' },
            }}
          >
            {t.SIGNUP_CTA}
          </Button>
        </Link>
      </div>

      {/* Footer */}
      <div className="demo-drawer-footer">
        <span className="demo-drawer-footer-text">Made with ♥ for educators</span>
      </div>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Header onToggle={handleDrawerToggle} activeModule="particify" currentDrawerWidth={drawerWidth} />
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="demo navigation"
      >
        {/* Mobile */}
        <Drawer
          container={window.document.body}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
    </Box>
  );
}
